import React, { useCallback, useMemo } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, CircularProgress, Grid } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import * as yup from 'yup'

import FacebookIcon from '~/assets/icons/facebook.svg'
import HomeIcon from '~/assets/icons/home.svg'
import MailIcon from '~/assets/icons/mail.svg'
import UserIcon from '~/assets/icons/profile.svg'
import UserEoIcon from '~/assets/icons/user-eo.svg'
import translate from '~/locale'
import {
  SendFacebookFormDto,
  sendFacebookForm,
} from '~/services/hubspot/sendFacebookForm'

import Input from '../FormFields/Input'
import InputAddress from '../FormFields/InputAddress'
import InputFacebookUrl from '../FormFields/InputFacebookUrl'
import Radio from '../FormFields/Radio'

import './styles.scss'

type FacebookFormProps = {
  onSuccess?: () => void
  classeName?: string
  autoFocus?: boolean
}

const FacebookForm: React.FC<FacebookFormProps> = ({
  onSuccess,
  classeName,
  autoFocus,
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha()

  const schema = useMemo(
    () =>
      yup.object().shape({
        firstName: yup
          .string()
          .required(translate('earlyaccess_validation_firstname_required')),
        lastName: yup
          .string()
          .required(translate('earlyaccess_validation_lastname_required')),
        address: yup.object().shape({
          name: yup.string(),
          value: yup
            .string()
            .required(translate('earlyaccess_validation_address_required')),
        }),
        email: yup
          .string()
          .email(translate('earlyaccess_validation_email_invalid'))
          .required(translate('earlyaccess_validation_email_required')),
        role: yup
          .string()
          .oneOf(
            ['living', 'working', 'elected-official'],
            translate('earlyaccess_validation_role_required'),
          )
          .required(translate('earlyaccess_validation_role_required')),
        isFacebookPageManager: yup.boolean(),
        facebookUrl: yup.string().when('isFacebookPageManager', {
          is: true,
          then: yup
            .string()
            .url(translate('earlyaccess_validation_facebook_url_invalid'))
            .required(
              translate('earlyaccess_validation_facebook_url_required'),
            ),
          otherwise: yup.string().optional(),
        }),
      }),
    [],
  )

  const formMethods = useForm<SendFacebookFormDto>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      address: {
        name: '',
        value: '',
      },
      email: '',
      facebookUrl: '',
      isFacebookPageManager: false,
      role: 'living',
    },
  })

  const [mutate, { isLoading, isSuccess, isError }] = useMutation(
    sendFacebookForm,
    {
      onSuccess: () => {
        onSuccess && onSuccess()
        formMethods.reset()
      },
    },
  )

  const handleSubmit = useCallback(
    async (formData: SendFacebookFormDto) => {
      if (!executeRecaptcha) {
        return
      }
      const token = await executeRecaptcha('earlyAccessForm')
      mutate({ ...formData, token })
    },
    [mutate, executeRecaptcha],
  )

  const isSubmitDisabled = useMemo(
    () => isLoading || !formMethods.formState.isValid || !executeRecaptcha,
    [isLoading, formMethods, executeRecaptcha],
  )

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        className={classeName}
      >
        {isSuccess && (
          <Alert severity="success" className="mb-4">
            {translate('earlyaccess_submit_success_message')}
          </Alert>
        )}

        {isError && (
          <Alert severity="error" className="mb-4">
            {translate('earlyaccess_submit_error_message')}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item sm={6}>
            <Input
              name="firstName"
              label={translate('earlyaccess_firstname_label')}
              icon={UserIcon}
              disabled={isLoading}
              autoFocus={autoFocus}
            />
          </Grid>
          <Grid item sm={6}>
            <Input
              name="lastName"
              label={translate('earlyaccess_lastname_label')}
              icon={UserIcon}
              disabled={isLoading}
            />
          </Grid>
          <Grid item sm={6}>
            <Input
              type="email"
              name="email"
              label={translate('earlyaccess_email_label')}
              icon={MailIcon}
              disabled={isLoading}
            />
          </Grid>
          <Grid item sm={6}>
            <InputAddress
              name="address"
              label={translate('earlyaccess_address_label')}
              icon={HomeIcon}
              disabled={isLoading}
            />
          </Grid>
          <Grid item sm={6} className="grid-mui-media">
            <Radio
              name="role"
              label={translate('earlyaccess_role_title')}
              icon={UserEoIcon}
              options={[
                {
                  name: translate('earlyaccess_role_resident'),
                  value: 'living',
                },
                {
                  name: translate('earlyaccess_role_employee'),
                  value: 'working',
                },
                {
                  name: translate('earlyaccess_role_EO'),
                  value: 'elected-official',
                },
              ]}
              disabled={isLoading}
            />
          </Grid>
          <Grid item sm={6}>
            <InputFacebookUrl
              checkboxName="isFacebookPageManager"
              inputName="facebookUrl"
              checkboxLabel={translate('earlyaccess_FBadmin_label')}
              inputLabel={translate('earlyaccess_FBurl_label')}
              icon={FacebookIcon}
              disabled={isLoading}
            />
          </Grid>
          <Button
            type="submit"
            className="btn-warning font-size-xl text-nowrap px-6 py-3 text-uppercase ml-auto"
            disabled={isSubmitDisabled}
          >
            <span>{translate('earlyaccess_button')}</span>
            {isLoading && (
              <CircularProgress className="ml-2" color="inherit" size={25} />
            )}
          </Button>
        </Grid>
      </form>
    </FormProvider>
  )
}

export default FacebookForm
